import enviro from 'enviro';
import { generatePath, Route, Switch, useLocation, useParams } from 'quartz-routing/react-router';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import ReactRouterScrollToTop from 'academy-common-lib/components/routing/ReactRouterScrollToTop';
import { ACRedirect } from 'academy-common-lib/components/ACRedirect';
import { selectACRoutes } from 'academy-common-lib/routes/academyRoutes';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import AchievementShareControls from './components/AchievementShareControls';
import AcademyOnboarding from './features/academyOnboarding/routes/AcademyOnboarding';
import HubspotPlaylistsAdmin from './features/hubspotPlaylists/components/hubspotPlaylistsAdmin/HubspotPlaylistsAdminMainPage';
import MyTeamCertificationsIndex from './features/myTeam/routes/MyTeamCertificationsIndex';
import MyTeamMembers from './features/myTeam/routes/MyTeamMembers';
import MyPlaylists from './routes/MyPlaylists';
import NotFound from './routes/NotFound';
import Playlist from './routes/Playlist';
import WatchLater from './routes/WatchLater';
import { PROFILE_URL_FRAGMENT, WATCH_LATER_URL_FRAGMENT } from './constants/Navigation.legacy';
import { useShowDevtools } from './features/devtools/devtoolsQueries';
import { MyLearningTabs } from './features/myLearning';
import { RedirectToMyLearning } from './features/myLearning/myLearningRouting';
import MyTeamCertificationProfile, { RedirectToCertificationProfileDefaultTab } from './features/myTeam/routes/MyTeamCertificationProfile';
import MyTeamCertificationsByTeam, { RedirectToCertificationsByTeamDefaultTab } from './features/myTeam/routes/MyTeamCertificationsByTeam';
import MyTeamOverview, { RedirectToMyTeamOverview } from './features/myTeam/routes/MyTeamOverview';
import MyTeamPlaylists, { RedirectToPlaylistsDefaultTab } from './features/myTeam/routes/MyTeamPlaylists';
import MyTeamContent, { RedirectToContentDefaultTab } from './features/myTeam/routes/MyTeamContent';
import { NavPageRoute } from './features/navigation/components/NavPageRoute.legacy';
import { EditProfile, MyProfile, UserProfile } from './routes/Profile';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AC_ROUTES = selectACRoutes();
const RedirectToWatchLater = () => {
  const location = useLocation();
  return /*#__PURE__*/_jsx(ACRedirect, {
    to: Object.assign({}, location, {
      pathname: generatePath(WATCH_LATER_URL_FRAGMENT)
    })
  });
};
const RedirectToNewOverview = () => {
  const location = useLocation();
  const {
    prefix,
    type,
    id,
    source,
    language
  } = useParams();
  const search = new URLSearchParams(location.search);
  const newParams = getOverviewModalParams({
    id: Number(id),
    type: type === 'course-overview' ? 'TRACK' : type === 'lesson-overview' ? 'LESSON' : 'SHORT_FORM_CONTENT',
    language,
    source
  });
  Object.entries(newParams).forEach(([key, value]) => {
    search.set(key, value);
  });
  return /*#__PURE__*/_jsx(ACRedirect, {
    to: Object.assign({}, location, {
      pathname: prefix,
      search: `?${search}`
    })
  });
};
const QaOnlyRoutes = Loadable({
  loader: () => import( /* webpackChunkName: "bundles/qaOnly" */'./components/QaOnly/QaOnlyRoutes').then(mod => mod.default)
});
const Devtools = Loadable({
  loader: () => import( /* webpackChunkName: "bundles/devtools" */'./features/devtools').then(mod => mod.Devtools),
  LoadingComponent: () => null
});
const RemoveTrailingSlash = () => {
  const location = useLocation();
  const {
    pathname
  } = location;
  return /*#__PURE__*/_jsx(ACRedirect, {
    to: Object.assign({}, location, {
      pathname: pathname.replace(/\/+$/, '')
    })
  });
};
export const MyPlaylistsRoutePaths = AC_ROUTES.MY_PLAYLISTS;
export const Routes = () => {
  const showDevtools = useShowDevtools();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), showDevtools && /*#__PURE__*/_jsx(Devtools, {}), /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        component: RemoveTrailingSlash,
        path: "/*(/+)"
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: AcademyOnboarding,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.academyOnboarding",
        path: AC_ROUTES.AC_ONBOARDING,
        shouldRenderCenteredContent: false
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: AchievementShareControls,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.shareAchievement",
        path: AC_ROUTES.SHARE_ACHIEVEMENT,
        shouldRenderFullScreen: true
      }), /*#__PURE__*/_jsx(Route, {
        path: AC_ROUTES.AWARD_SHARE_REDIRECT,
        render: () => /*#__PURE__*/_jsx(ACRedirect, {
          to: PROFILE_URL_FRAGMENT
        })
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToNewOverview,
        path: AC_ROUTES.LEGACY_OVERVIEW
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToMyTeamOverview,
        exact: true,
        path: AC_ROUTES.LEGACY_MY_TEAM
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamOverview,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeam",
        path: AC_ROUTES.MY_TEAM
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamCertificationsIndex,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamCertifications",
        path: AC_ROUTES.MY_TEAM_CERTIFICATIONS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamMembers,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamMembers",
        path: AC_ROUTES.MY_TEAM_MEMBERS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamCertificationProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.certification",
        path: AC_ROUTES.MY_TEAM_CERTIFICATION_PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: RedirectToCertificationProfileDefaultTab,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.certification",
        path: AC_ROUTES.MY_TEAM_CERTIFICATION_PROFILE_REDIRECT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamCertificationsByTeam,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamCertificationsByTeam",
        path: AC_ROUTES.MY_TEAM_CERTIFICATIONS_BY_TEAM
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: RedirectToCertificationsByTeamDefaultTab,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamCertificationsByTeam",
        path: AC_ROUTES.MY_TEAM_CERTIFICATIONS_BY_TEAM_REDIRECT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamPlaylists,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamPlaylists",
        path: AC_ROUTES.MY_TEAM_PLAYLISTS
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: RedirectToPlaylistsDefaultTab,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamPlaylists",
        path: AC_ROUTES.MY_TEAM_PLAYLISTS_REDIRECT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyTeamContent,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamContent",
        path: AC_ROUTES.MY_TEAM_CONTENT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: RedirectToContentDefaultTab,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myTeamContent",
        path: AC_ROUTES.MY_TEAM_CONTENT_REDIRECT
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: EditProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.editProfile",
        path: AC_ROUTES.EDIT_PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: UserProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.userProfile",
        path: AC_ROUTES.USER_PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyProfile,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.myProfile",
        path: AC_ROUTES.PROFILE
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: MyLearningTabs,
        pageTitleI18nKey: "learningCenter.pageTitles.myLearning",
        path: AC_ROUTES.MY_LEARNING
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToMyLearning,
        exact: false,
        path: AC_ROUTES.LEGACY_MY_LEARNING
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.hubspotPlaylistsAdmin",
        path: AC_ROUTES.HUBSPOT_PLAYLISTS_ADMIN,
        children: /*#__PURE__*/_jsx(HubspotPlaylistsAdmin, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: AC_ROUTES.SAVED_ITEMS_PLAYLISTS,
        children: /*#__PURE__*/_jsxs(Switch, {
          children: [/*#__PURE__*/_jsx(NavPageRoute, {
            exact: true,
            pageTitleI18nKey: "learningCenter.pageTitles.myPlaylists",
            path: AC_ROUTES.MY_PLAYLISTS,
            children: /*#__PURE__*/_jsx(MyPlaylists, {})
          }), /*#__PURE__*/_jsx(NavPageRoute, {
            component: Playlist,
            exact: true,
            pageTitleI18nKey: "learningCenter.pageTitles.playlist",
            path: AC_ROUTES.PLAYLIST,
            shouldRenderCenteredContent: false
          })]
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: AC_ROUTES.SAVED_ITEMS,
        children: /*#__PURE__*/_jsx(Switch, {
          children: /*#__PURE__*/_jsx(NavPageRoute, {
            component: RedirectToWatchLater,
            pageTitleI18nKey: "learningCenter.pageTitles.watchLater",
            path: AC_ROUTES.LEGACY_WATCH_LATER
          })
        })
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: WatchLater,
        pageTitleI18nKey: "learningCenter.pageTitles.watchLater",
        path: AC_ROUTES.WATCH_LATER
      }), enviro.isQa() && /*#__PURE__*/_jsx(Route, {
        component: QaOnlyRoutes,
        path: AC_ROUTES.QA_ONLY
      }), /*#__PURE__*/_jsx(NavPageRoute, {
        component: NotFound,
        pageTitleI18nKey: "learningCenter.pageTitles.notFound",
        path: "*"
      })]
    }), /*#__PURE__*/_jsx(Route, {
      component: ReactRouterScrollToTop,
      path: AC_ROUTES.MY_TEAM
    })]
  });
};
export default Routes;